import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BookingBaggageNote, FooterLockIcon } from './BookingFlightIcons';
import BookingPassengerSection from './BookingPassengerSection';
import BookingSiteHeader from './BookingSiteHeader';
import BookingConfirmModal from './BookingConfirmModal';
import BookingTabBar from './BookingTabBar';
import { fetchAgentPublic, patchQuoteBooking } from '../lib/api';
import { airlineLogoUrl } from '../lib/airlineLogo';
import {
  bookingFormsToQuotePassengers,
  initialBookingPassengerForms,
  validateBookingPassengerForms,
} from '../lib/bookingPassengers';
import type { BookingPassengerFormRow } from '../lib/bookingPassengers';
import { airportCityLabel, airportCodeLabel, airportExpandDiffersFromCode, airportExpandLabel } from '../lib/airportLabels';
import {
  formatDurationColon,
  formatLegDateLong,
  formatLegDateShort,
  formatMoney,
  formatTime12h,
} from '../lib/checkoutFormat';
import { buildPaymentUrl } from '../lib/hosts';
import { generateEticketNumber } from '../lib/skyluxReceiptIds';
import { saveQuote } from '../lib/skyluxStore';
import { formatLayoverLabel, segmentMealLabel } from '../lib/bookingFlightDisplay';
import type { Agent, FlightLayover, FlightLeg, FlightSegment, QuoteRecord } from '../types';
import '../styles/booking.css';

const COMPANY_FOOTER = 'Luxury Travel Services LLC, 901 N. Market St., Ste 705 Wilmington, DE 19801';

function RouteArrowIcon() {
  return (
    <svg className="booking-leg-card__route-arrow" focusable="false" viewBox="0 0 20 9" aria-hidden="true">
      <path
        d="M19.75826,4.97169l-2.80124,3.67964c-0.17091,0.2245 -0.44809,0.2245 -0.619,0c-0.17094,-0.22455 -0.17094,-0.58856 0,-0.81311l2.05404,-2.69814v0l-17.62454,0.16839c-0.24171,0 -0.43769,-0.25743 -0.43769,-0.57495c0,-0.31746 0.19598,-0.57494 0.43769,-0.57494l17.62454,-0.16839v0l-2.05397,-2.69814c-0.17095,-0.22455 -0.17095,-0.58856 0,-0.81311c0.08544,-0.11218 0.19749,-0.16839 0.3095,-0.16839c0.11201,0 0.22403,0.05621 0.3095,0.16839l2.80117,3.67964c0.17094,0.22454 0.17094,0.58855 0,0.81311z"
        fill="currentColor"
      />
    </svg>
  );
}

function StepCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden="true" className="booking-step-check">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
}

function formatDurationShort(mins?: number | null): string {
  if (mins == null || !Number.isFinite(mins) || mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function legSegments(leg: FlightLeg): FlightSegment[] {
  if (leg.segments && leg.segments.length > 0) return leg.segments;
  return [
    {
      airline: leg.airline,
      flightNumber: leg.flightNumber,
      departure: leg.departure,
      arrival: leg.arrival,
      departureName: leg.departureName,
      arrivalName: leg.arrivalName,
      departureTime: leg.departureTime,
      arrivalTime: leg.arrivalTime,
      duration: leg.totalDuration,
      travelClass: undefined,
      airplane: undefined,
    },
  ];
}

function useQuoteCountdown(createdAt: string): string {
  const deadline = useMemo(() => new Date(createdAt).getTime() + 24 * 60 * 60 * 1000, [createdAt]);
  const [left, setLeft] = useState(() => Math.max(0, deadline - Date.now()));

  useEffect(() => {
    const id = window.setInterval(() => setLeft(Math.max(0, deadline - Date.now())), 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

type FlightSegmentRowProps = {
  seg: FlightSegment;
  travelDate?: string;
};

function segmentArriveDate(travelDate: string | undefined, duration?: number): string {
  if (!travelDate) return '';
  if (!duration) return formatLegDateShort(travelDate);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(travelDate.trim());
  if (!m) return formatLegDateShort(travelDate);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0));
  d.setUTCMinutes(d.getUTCMinutes() + duration);
  return formatLegDateShort(d.toISOString().slice(0, 10));
}

function LayoverRow({ layover }: { layover: FlightLayover }) {
  return (
    <div className="booking-layover-row" role="note">
      {formatLayoverLabel(layover)}
    </div>
  );
}

function BookingAirportCode({ code, airportName }: { code: string; airportName?: string }) {
  const displayCode = airportCodeLabel(code);
  const expandLabel = React.useMemo(() => {
    const fromApi = airportName?.trim();
    if (fromApi && fromApi.toUpperCase() !== displayCode.toUpperCase()) return fromApi;
    return airportExpandLabel(code);
  }, [airportName, code, displayCode]);
  const canExpand = React.useMemo(() => {
    const fromApi = airportName?.trim();
    if (fromApi && fromApi.toUpperCase() !== displayCode.toUpperCase()) return true;
    return airportExpandDiffersFromCode(code);
  }, [airportName, code, displayCode]);
  const [open, setOpen] = React.useState(false);

  return (
    <span className="booking-time-block__airport">
      <button
        type="button"
        className="booking-time-block__code"
        aria-expanded={open}
        disabled={!canExpand}
        aria-label={
          canExpand
            ? open
              ? `Hide full name for ${displayCode}`
              : `Show full name for ${displayCode}`
            : displayCode
        }
        onClick={() => canExpand && setOpen((prev) => !prev)}
      >
        {displayCode}
      </button>
      {open && canExpand ? (
        <span className="booking-time-block__airport-full" role="note">
          {expandLabel}
        </span>
      ) : null}
    </span>
  );
}

function FlightSegmentRow({ seg, travelDate }: FlightSegmentRowProps) {
  const logo = airlineLogoUrl(seg.flightNumber);
  const travelClass = seg.travelClass?.trim() || 'Economy';
  const meal = segmentMealLabel(seg);
  const depCity = airportCityLabel(seg.departure);
  const arrCity = airportCityLabel(seg.arrival);
  const arriveDate = segmentArriveDate(travelDate, seg.duration);

  return (
    <div className="booking-segment-row">
      <div className="booking-segment__schedule-col">
        <div className="booking-segment__schedule-panel">
          <div className="booking-time-slot booking-time-slot--dep">
            <div className="booking-time-slot__rail-col" aria-hidden>
              <span className="booking-time-slot__marker" />
            </div>
            <div className="booking-time-slot__body">
              <div className="booking-time-block">
                <span className="booking-time-block__time">{formatTime12h(seg.departureTime)}</span>
                <span className="booking-time-block__date">{formatLegDateShort(travelDate)}</span>
                <span className="booking-time-block__location">
                  <span>{depCity}, </span>
                  <BookingAirportCode code={seg.departure} airportName={seg.departureName} />
                </span>
              </div>
            </div>
          </div>
          <div className="booking-time-slot booking-time-slot--mid">
            <div className="booking-time-slot__rail-col" aria-hidden>
              <span className="booking-time-slot__rail-track" />
            </div>
            <div className="booking-time-slot__mid-panel">
              <div className="booking-time-mid__flight">
                <span>Flight time: </span>
                <span>{formatDurationColon(seg.duration)}</span>
              </div>
            </div>
          </div>
          <div className="booking-time-slot booking-time-slot--arr">
            <div className="booking-time-slot__rail-col" aria-hidden>
              <span className="booking-time-slot__marker" />
            </div>
            <div className="booking-time-slot__body">
              <div className="booking-time-block">
                <span className="booking-time-block__time">{formatTime12h(seg.arrivalTime)}</span>
                <span className="booking-time-block__date">{arriveDate}</span>
                <span className="booking-time-block__location">
                  <span>{arrCity}, </span>
                  <BookingAirportCode code={seg.arrival} airportName={seg.arrivalName} />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {meal ? (
        <div className="booking-segment__meal-col" aria-label={`Meal: ${meal}`}>
          <span className="booking-segment__meal-value">
            <img
              className="booking-segment__meal-image"
              src="/payment/image.png"
              alt=""
              aria-hidden="true"
            />
            {meal}
          </span>
        </div>
      ) : null}
      <div className="booking-segment__airline-col">
        <div className="booking-segment__airline-inner">
          {logo ? <img className="booking-segment__logo" src={logo} alt={seg.airline} /> : null}
          <div className="booking-segment__flight">
            <span className="booking-segment__flight-no">{seg.flightNumber}</span> | {travelClass}
          </div>
          <div className="booking-segment__sub">
            <span>{seg.airline}</span>
          </div>
          <div className="booking-segment__sub">
            <span>{(seg.airplane ?? '').toLowerCase() || '\u00a0'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
type LegCardProps = { leg: FlightLeg };

function LegCard({ leg }: LegCardProps) {
  const segs = legSegments(leg);
  const travelMins = leg.totalDuration ?? segs.reduce((s, x) => s + (x.duration ?? 0), 0);
  const depCity = airportCityLabel(leg.departure);
  const arrCity = airportCityLabel(leg.arrival);

  return (
    <li className="booking-leg-card">
      <div className="booking-leg-card__head">
        <div className="booking-leg-card__head-inner">
          <h3 className="booking-leg-card__route">
            {depCity}
            <RouteArrowIcon />
            {arrCity}
          </h3>
          <div className="booking-leg-card__meta-row">
            <h3 className="booking-leg-card__date">{formatLegDateLong(leg.travelDate)}</h3>
            <div className="booking-leg-card__travel-wrap">
              <div>Travel time: {formatDurationShort(travelMins)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="booking-leg-card__body">
        {segs.map((seg, i) => (
          <React.Fragment key={`${seg.flightNumber}-${i}`}>
            <FlightSegmentRow seg={seg} travelDate={leg.travelDate} />
            {i < segs.length - 1 && leg.layovers?.[i] ? <LayoverRow layover={leg.layovers[i]!} /> : null}
          </React.Fragment>
        ))}
      </div>
    </li>
  );
}

export type BookingViewProps = {
  quote: QuoteRecord;
};

const BookingView = ({ quote: quoteProp }: BookingViewProps) => {
  const [quote, setQuote] = useState(quoteProp);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [passengerForms, setPassengerForms] = useState<BookingPassengerFormRow[]>(() =>
    initialBookingPassengerForms(quoteProp),
  );
  const [passengerError, setPassengerError] = useState<string | null>(null);
  const [showPassengerFieldErrors, setShowPassengerFieldErrors] = useState(false);
  const [booking, setBooking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const countdown = useQuoteCountdown(quote.createdAt);
  const f = quote.flight;
  const bagCount = quote.bags != null && Number.isFinite(quote.bags) ? Math.max(0, Math.min(9, Math.round(quote.bags))) : 0;
  const pricePerAdult = quote.pricePerPerson;
  const paymentUrl = buildPaymentUrl(quote.id);
  const isPaid = quote.paymentStatus === 'paid';
  const logo = airlineLogoUrl(f.flightNumber);
  const refTravelDate = f.travelDate ?? f.returnLeg?.travelDate;

  useEffect(() => {
    setQuote(quoteProp);
  }, [quoteProp]);

  useEffect(() => {
    setPassengerForms(initialBookingPassengerForms(quote));
    setPassengerError(null);
    setShowPassengerFieldErrors(false);
  }, [quote]);

  const handleBookClick = useCallback(() => {
    const err = validateBookingPassengerForms(passengerForms);
    if (err) {
      setPassengerError(err);
      setShowPassengerFieldErrors(true);
      return;
    }
    setPassengerError(null);
    setShowPassengerFieldErrors(false);
    setConfirmOpen(true);
  }, [passengerForms]);

  const handleEditPassengerFromConfirm = useCallback((passengerId: string) => {
    setConfirmOpen(false);
    window.requestAnimationFrame(() => {
      document.getElementById(`passenger-${passengerId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const persistPassengers = useCallback(async () => {
    const existingTickets = (quote.passengers ?? []).map((p) => p.eTicketNumber?.trim()).filter(Boolean) as string[];
    const passengers = bookingFormsToQuotePassengers(passengerForms, refTravelDate, existingTickets).map((p, i) => ({
      ...p,
      eTicketNumber: p.eTicketNumber ?? existingTickets[i] ?? generateEticketNumber(),
    }));
    const updated = await patchQuoteBooking(quote.id, { passengers });
    saveQuote(updated);
    setQuote(updated);
    try {
      sessionStorage.setItem(
        `skylux_checkout_passengers_${updated.id}`,
        JSON.stringify(updated.passengers ?? []),
      );
    } catch {
      /* ignore quota / private mode */
    }
    return updated;
  }, [passengerForms, quote.id, quote.passengers, refTravelDate]);

  const handleSaveDetails = useCallback(async () => {
    setBooking(true);
    try {
      await persistPassengers();
      setSaveNotice('Passenger details saved. You can return anytime using this link.');
      setConfirmOpen(false);
    } catch (e) {
      setPassengerError(e instanceof Error ? e.message : 'Could not save passenger details. Try again.');
    } finally {
      setBooking(false);
    }
  }, [persistPassengers]);

  const handleProceedToPayment = useCallback(async () => {
    setBooking(true);
    try {
      const updated = await persistPassengers();
      window.location.assign(buildPaymentUrl(updated.id));
    } catch (e) {
      setPassengerError(e instanceof Error ? e.message : 'Could not save passenger details. Try again.');
      setBooking(false);
      setConfirmOpen(false);
    }
  }, [persistPassengers, quote.id]);

  const handleConfirmClose = useCallback(() => {
    if (booking) return;
    setConfirmOpen(false);
  }, [booking]);

  useEffect(() => {
    let cancelled = false;
    void fetchAgentPublic(quote.agentId)
      .then((a) => {
        if (!cancelled) setAgent(a);
      })
      .catch(() => {
        if (!cancelled) {
          setAgent({
            id: quote.agentId,
            name: 'SkyLux Travel',
            nickname: 'Agent',
            email: 'support@skyluxtravel.com',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [quote.agentId]);

  const agentDisplay = agent ?? {
    id: quote.agentId,
    name: 'SkyLux Travel',
    nickname: 'Agent',
    email: 'support@skyluxtravel.com',
  };

  const legs: { leg: FlightLeg; label?: string }[] = [{ leg: f }];
  if (f.returnLeg) legs.push({ leg: f.returnLeg, label: 'Return' });

  return (
    <div className="skylux-booking">
      <link
        href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&family=PT+Sans:wght@400;700&family=PT+Serif:wght@400;700&display=swap"
        rel="stylesheet"
      />

      <BookingSiteHeader agent={agentDisplay} homeHref={quote.bookingUrl} />

      <main className="booking-main">
        <section className="booking-hero" aria-labelledby="booking-hero-title">
          <div className="booking-hero__inner">
            <div>
              <h2 id="booking-hero-title" className="booking-hero__title">
                Please, choose the flight and check passenger details
              </h2>
              <p className="booking-hero__subtitle">Tickets at a good price are almost in your hands!</p>
            </div>
            <div className="booking-steps" aria-label="Booking progress">
              {[
                ['Request a quote', 'done'],
                ['Review flight options', 'done'],
                ['Choose best flight', 'active'],
                ['Complete payment', 'disabled'],
              ].map(([label, state], stepIndex) => (
                <div
                  key={label}
                  className={`booking-step${state === 'done' ? ' is-done' : ''}${state === 'active' ? ' is-active' : ''}${state === 'disabled' ? ' is-disabled' : ''}`}
                >
                  <div className="booking-step__icon">
                    {state === 'done' ? <StepCheckIcon /> : stepIndex + 1}
                  </div>
                  <span className="booking-step__label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <BookingTabBar
          tabs={[
            {
              index: 1,
              airline: f.airline,
              flightNumber: f.flightNumber,
              price: pricePerAdult,
              selected: true,
            },
          ]}
        />

        <div className="booking-content">
          <div className="booking-content__grid">
            <div className="booking-flight-details">
              <h2 className="booking-section-title booking-section-title--flight">
                <span className="booking-section-title__label">Flight details</span>
                <BookingBaggageNote count={bagCount} />
              </h2>

              <ul className="booking-flight-list">
                {legs.map(({ leg, label }) => (
                  <LegCard key={`${leg.departure}-${leg.arrival}-${label ?? 'out'}`} leg={leg} />
                ))}
              </ul>

              <footer className="booking-leg-footer">{COMPANY_FOOTER}</footer>
            </div>

            <div className="booking-passenger-column">
              {saveNotice ? (
                <p className="booking-save-notice" role="status">
                  {saveNotice}
                </p>
              ) : null}
              <BookingPassengerSection
                rows={passengerForms}
                readOnly={isPaid}
                showFieldErrors={showPassengerFieldErrors}
                onChange={(rows) => {
                  setPassengerForms(rows);
                  if (passengerError) setPassengerError(null);
                }}
                error={passengerError}
              />

              <div className="booking-disclaimer">
                <svg viewBox="0 0 18 18" width={18} height={18} aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M10.771 5.734c-.269.25-.593.375-.971.375a1.39 1.39 0 0 1-.976-.375 1.197 1.197 0 0 1-.406-.909c0-.354.137-.659.406-.91a1.38 1.38 0 0 1 .976-.38c.378 0 .703.126.971.38.269.251.404.556.404.91 0 .356-.135.66-.404.91zm.102 8.288c-.463.183-.832.322-1.108.418a2.925 2.925 0 0 1-.962.144c-.56 0-.997-.137-1.308-.41a1.325 1.325 0 0 1-.466-1.042c0-.164.012-.332.035-.502.023-.171.06-.363.112-.579l.58-2.048c.05-.196.095-.383.13-.557a2.47 2.47 0 0 0 .052-.482c0-.26-.055-.443-.162-.546-.109-.103-.314-.153-.62-.153-.149 0-.303.022-.46.068a6.547 6.547 0 0 0-.403.134l.153-.63c.38-.155.743-.288 1.09-.397.346-.112.674-.167.982-.167.557 0 .987.136 1.29.404.3.27.452.619.452 1.049 0 .089-.01.246-.031.47-.021.224-.06.43-.116.618l-.577 2.042c-.047.163-.09.35-.127.56-.038.21-.056.37-.056.477 0 .271.06.457.182.555.12.098.332.148.63.148.141 0 .299-.025.477-.074.177-.049.305-.092.386-.13zM9 .074a9 9 0 1 0 0 18 9 9 0 0 0 0-18z"
                  />
                </svg>
                <div>The quoted prices are not guaranteed until the tickets are issued</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="booking-footer" role="contentinfo">
        <div className="booking-footer__container">
          <div className="booking-footer__grid">
            <div className="booking-footer__airline-col">
              <div className="booking-footer__airline-row">
                {logo ? (
                  <div className="booking-footer__logo-cell">
                    <img src={logo} alt={f.airline} />
                  </div>
                ) : null}
                <div className="booking-footer__name-cell">
                  <span className="booking-footer__airline-name">1. {f.airline}</span>
                </div>
              </div>
            </div>
            <div className="booking-footer__actions-col">
              <div className="booking-footer__price-block">
                <FooterLockIcon />
                <div className="booking-footer__price-text">
                  <span className="booking-footer__price">${formatMoney(pricePerAdult)}</span>
                  <span className="booking-footer__price-label">Total per adult</span>
                </div>
              </div>
              <div className="booking-footer__book-wrap">
                {isPaid ? (
                  <a className="booking-footer__book-btn" href={paymentUrl}>
                    <span className="booking-footer__book-label">View payment</span>
                  </a>
                ) : (
                  <button
                    type="button"
                    className="booking-footer__book-btn"
                    disabled={booking}
                    aria-label={`Book, time left ${countdown}`}
                    onClick={handleBookClick}
                  >
                    <div className="booking-footer__book-inner">
                      {!booking ? (
                        <div className="booking-footer__countdown-row">
                          <span className="booking-footer__countdown">{countdown}</span>
                        </div>
                      ) : null}
                      <div className="booking-footer__book-label">{booking ? 'Saving…' : 'Book'}</div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </footer>

      <BookingConfirmModal
        open={confirmOpen}
        passengers={passengerForms}
        proceeding={booking}
        onClose={handleConfirmClose}
        onEditPassenger={handleEditPassengerFromConfirm}
        onSaveDetails={() => void handleSaveDetails()}
        onProceed={() => void handleProceedToPayment()}
      />
    </div>
  );
};

export default BookingView;
